import { useId } from 'react'
import { queryClient } from '@shared/api/query-client'
import s from './payout-card.module.scss'
import classNames from 'classnames'
import { TZDate } from '@date-fns/tz'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useSendReceipt } from '@entities/profile/api/useSendReceipt'
import { useProfilePackages } from '@features/teacher/packages/api/useProfilePackages'
import { Tooltip } from 'react-tooltip'
import { IconInfoCircle } from '@tabler/icons-react'
import { TableCell, TableRow } from '@shared/ui/table/table'
import { SupportButton } from '@shared/ui/support-button'
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji'

const getAvatarUrl = (avatar) => {
	if (!avatar) return null
	if (typeof avatar === 'string') return process.env.REACT_APP_STORAGE_URL + avatar
	return avatar?.image ? process.env.REACT_APP_STORAGE_URL + avatar.image : null
}

const STATUS = {
	"CHECKED": { label: 'Ожидание подтверждения', className: 'pending' },
	"CREDIT_CHECKING": { label: 'Ожидание зачисления', className: 'pending' },
	"COMPLETED": { label: 'Выплата прошла', className: 'success' },
}

const formatAmount = (value) =>
	new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format((value || 0) / 100)

export const PayoutCard = ({ teacher_receipt, order, order_id, amount, id, status, isPending, isMobile }) => {
	const {data: profile} = queryClient.getQueryData(['profile'])
	const receiptInputId = useId()
	const receiptRejectTooltipId = useId()
	const sendReceipt = useSendReceipt()

	const studentName = order?.payer_name
	const defaultAvatar = process.env.REACT_APP_STORAGE_URL + 'images/default_avatar.png'
	const avatarSrc = getAvatarUrl(order?.payer?.avatar) || defaultAvatar
	const onImgError = (e) => { e.currentTarget.onerror = null; e.currentTarget.src = defaultAvatar }
	// Сумма к получению: discounted_amount - withheld_amount
	const payoutAmount = isPending
		? (order?.discounted_amount ?? order?.amount ?? 0) - (order?.withheld_amount ?? 0)
		: (amount ?? 0)

	// Тип услуги: одиночное занятие или пакет занятий
	const serviceItem = order?.items?.[0]
	const serviceType = serviceItem?.service_type
	const isPack = serviceType === 'appointments_pack'
	const dateLabel = isPack ? 'Дата покупки' : 'Дата занятия'
	const appointment = serviceItem?.appointment
	// ID занятия для прикрепления чека: из вложенного appointment, иначе service_id для одиночных занятий
	const appointmentId = appointment?.id ?? (serviceType === 'appointment' ? serviceItem?.service_id : null)
	const instrumentName = appointment?.instrument?.name
	// Дата: дата занятия, если есть, иначе дата заказа
	const dateSrc = appointment?.starts_at ?? order?.created_at
	const date = dateSrc ? new TZDate(dateSrc, profile?.timezone) : null
	const dateStr = date ? format(date, 'dd MMMM yyyy', { locale: ru }) : '—'
	const { data: profilePacks } = useProfilePackages()
	const pack = isPack
		? (serviceItem?.pack ?? profilePacks?.find((p) => p.id === serviceItem?.service_id))
		: null

	// Чек преподавателя: из вложенного занятия, иначе с уровня payout (старый формат)
	const receipt = appointment?.teacher_receipt ?? teacher_receipt
	const isRejectedReceipt = receipt?.status?.toLowerCase() === 'rejected'

	const renderStatus = () => {
		if (isPending) {
			return (
				<div className={classNames(s.textAndStatus, s.wait)}>
					<span className={s.indicator} />
					<span className={s.statusText}>Ожидает выплаты</span>
				</div>
			)
		}

		if (status === 'REJECTED') {
			return (
				<SupportButton
					label="Ошибка"
					size="small"
					theme="secondary"
					className={s.supportWrapper}
					buttonClassName={classNames(s.textAndStatus, s.cancel)}
					data-tooltip-id="tooltip-canceled-payout"
				/>
			)
		}

		const st = STATUS[status]

		return (
			<div className={classNames(s.textAndStatus, st && s[st.className])}>
				<span className={s.indicator} />
				<span className={s.statusText}>{st ? st.label : status}</span>
			</div>
		)
	}

	const handleReceiptChange = (event) => {
		const file = event.target.files?.[0]

		if (!file) {
			return
		}

		sendReceipt.mutate({
			appointment_id: appointmentId,
			data: file
		})

		event.target.value = ''
	}

	const receiptBlock = !isPending && appointmentId && status === 'COMPLETED' ? (
		receipt && !isRejectedReceipt ? (
			<a
				className={s.receiptLink}
				href={process.env.REACT_APP_STORAGE_URL + receipt?.receipt_file}
				rel="noreferrer"
				target="_blank"
			>
				Чек
			</a>
		) : (
			<>
				{isRejectedReceipt ? (
					<div className={s.receiptRejectState}>
						<div className={s.receiptRejectMessage}>
							<span>Чек отклонен</span>
							<span
								className={s.receiptRejectIcon}
								data-tooltip-id={receiptRejectTooltipId}
								data-tooltip-content={receipt?.reject_reason || 'Причина отклонения не указана'}
							>
								<IconInfoCircle size={16} />
							</span>
							<Tooltip id={receiptRejectTooltipId} />
						</div>
					</div>
				) : null}
				<label
					htmlFor={receiptInputId}
					className={classNames(s.receiptButton, {
						[s.disabled]: sendReceipt.isPending
					})}
				>
					{sendReceipt.isPending ? 'Отправка...' : isRejectedReceipt ? 'Отправить заново' : 'Отправить чек'}
				</label>
				<input
					id={receiptInputId}
					type="file"
					className={s.receiptInput}
					accept=".pdf,image/*"
					onChange={handleReceiptChange}
					disabled={sendReceipt.isPending}
				/>
			</>
		)
	) : null

	if (isMobile) {
		return (
			<TableRow className={s.card}>
				<div className={s.mainInfo}>
					<div className={s.studentInfo}>
						<img src={avatarSrc} onError={onImgError} alt="" />
						<div className={s.studentText}>
							<span className={s.studentLabel}>Ученик</span>
							<span className={s.studentName}>{studentName || '—'}</span>
							{instrumentName && (
								<span className={s.instrument}>
									<TextWithEmoji text={instrumentName} />
								</span>
							)}
							{isPack && (
								<span className={s.packBadge}>
									{pack?.lessons_count ? `Пакет · ${pack.lessons_count} занятий` : 'Пакет занятий'}
								</span>
							)}
						</div>
					</div>
					{renderStatus()}
				</div>
				<div className={s.line}>
					<span className={s.label}>{dateLabel}</span>
					<span className={s.value}>{dateStr}</span>
				</div>
				<div className={s.line}>
					<span className={s.label}>Заказ</span>
					<span className={s.value}>#{order_id}</span>
				</div>
				<div className={s.line}>
					<span className={s.label}>Получено</span>
					<span className={s.amount}>{formatAmount(payoutAmount)} ₽</span>
				</div>
				{receiptBlock && <div className={s.receiptMobile}>{receiptBlock}</div>}
			</TableRow>
		)
	}

	return (
		<TableRow className={s.card}>
			<TableCell data-label={'Заказ'}>
				<div className={s.td}>
					<span className={s.orderId}>#{order_id}</span>
				</div>
			</TableCell>
			<TableCell data-label={'Статус'}>
				<div className={s.td}>
					{renderStatus()}
				</div>
			</TableCell>
				<TableCell data-label={'Ученик'}>
				<div className={s.td}>
					<div className={s.teacher}>
						<img src={avatarSrc} onError={onImgError} alt="" />
						<div className={s.teacherText}>
							<span className={s.teacherName}>{studentName || '—'}</span>
							{instrumentName && (
								<span className={s.instrument}>
									<TextWithEmoji text={instrumentName} />
								</span>
							)}
						</div>
						{isPack && (
							<span className={s.packBadge}>
								{pack?.lessons_count ? `Пакет · ${pack.lessons_count} занятий` : 'Пакет занятий'}
							</span>
						)}
					</div>
				</div>
			</TableCell>
			<TableCell data-label={dateLabel}>
				<div className={s.td}>
					<span className={s.date}>{dateStr}</span>
				</div>
			</TableCell>
			<TableCell data-label={'Получено'} className={s.amountCell}>
				<div className={s.td}>
					<span className={s.price}>{formatAmount(payoutAmount)} ₽</span>
				</div>
			</TableCell>
			<TableCell data-label={'Чек'}>
				<div className={s.td}>
					<div className={s.receiptWrapper}>
						{receiptBlock}
					</div>
				</div>
			</TableCell>
		</TableRow>
	)
}
